import { Close, Edit, ExpandLess, ExpandMore, ImageOutlined, PlaylistAdd, Save, TableChart } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, Chip, Collapse, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Modal, Select, SelectChangeEvent, Switch, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import DialogQuiz from '../../Components/Modal/DialogQuiz'
import EmptyState from '../../Components/EmptyState'
import ModalHeader from '../../Components/Modal/ModalHeader'
import { PriceTableLayout } from '../../Model/FullPlaylistModel'
import { Media } from '../../Model/MediaModel'
import { ItemType, Playlist, PlaylistDTO, PlaylistItems, ScheduleConfig } from '../../Model/PlaylistModel'
import { PriceTable } from '../../Model/PriceTableModel'
import { Template } from '../../Model/TemplateModel'
import { useBroadcastPlaylistUpdateMutation } from '../../service/DeviceSessionService'
import { useFindAllMediaQuery } from '../../service/MediaService'
import { toPlaylistDTO, useCreatePlaylistMutation, useFindAllPlaylistsQuery, useUpdatePlaylistMutation } from '../../service/PlaylistService'
import { useCreatePriceTableLayoutMutation, useUpdatePriceTableLayoutsMutation } from '../../service/PriceTableLayoutService'
import { useFindAllPriceTablesQuery } from '../../service/PriceTableService'
import { useFindAllProductsQuery } from '../../service/ProductService'
import { useFindAllDefaultTemplatesQuery, useFindAllTemplatesQuery } from '../../service/TemplateService'
import { useAppSelector } from '../../store/hooks'
import { clearPageTitle, setPageTitle } from '../../store/reducers/ui'
import config from '../../Util/config'
import { createPriceTableDisplays } from '../../Util/parseHTML'
import CardMidia from './CardMidia'
import CardTable from './CardTable'
import EditHelper from './EditHelper'
import MediaPickerDialog from './MediaPickerDialog'
import PriceTablePickerDialog from './PriceTablePickerDialog'
import ScheduleModal from './ScheduleModal'

const MAX_SEC = config.FEATURE.MAX_SECONDS
const MIN_SEC = config.FEATURE.MIN_SECONDS
const MAX_ITEMS = config.FEATURE.MAX_PLAYLIST_ITEMS

function formatDuration(totalSeconds: number): string {
  if (totalSeconds === 0) return '0s'
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  if (m === 0) return `${s}s`
  if (s === 0) return `${m}min`
  return `${m}min ${s}s`
}

const PlaylistEditPage = () => {
  const { id = null } = useParams()
  const { state: locationState } = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Redux
  const { templates } = useAppSelector(state => ({ templates: state.template }))
  const { medias } = useAppSelector(state => ({ medias: state.media }))
  const { priceTables } = useAppSelector(state => ({ priceTables: state.priceTable }))
  const { playlists } = useAppSelector(state => ({ playlists: state.playlist }))
  const { products } = useAppSelector(state => ({ products: state.product }))
  const user = useAppSelector(state => state.auth.user)
  const pageTitle = useAppSelector(state => state.ui.pageTitle)

  // UI state
  const [openCancelDialog, setOpenCancelDialog] = useState(false)
  const [openMediaPicker, setOpenMediaPicker] = useState(false)
  const [openTablePicker, setOpenTablePicker] = useState(false)
  const [openHelp, setOpenHelp] = useState(false)
  const [configExpanded, setConfigExpanded] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [scheduleModalItem, setScheduleModalItem] = useState<PlaylistItems | null>(null)
  const isEditing = id !== '0'

  // Playlist state
  const [idCounter, setIdCounter] = useState(0)
  const [playlistItens, setItems] = useState<PlaylistItems[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<Template | undefined>()
  const [selectedSeconds, setSelectedSeconds] = useState(10)
  const [isTimeFixed, setFixedTime] = useState(false)
  const [isTemplateFixed, setFixedTemplate] = useState(false)
  const [rotation, setRotation] = useState(0)

  // API
  const userId = user?.id?.toString() || ''
  useFindAllMediaQuery(userId, { skip: medias.length > 0 })
  const { isSuccess: ptLoaded }   = useFindAllPriceTablesQuery(userId, { skip: priceTables.length > 0 })
  const { isSuccess: tmplLoaded } = useFindAllTemplatesQuery(userId, { skip: templates.length > 0 })
  useFindAllDefaultTemplatesQuery(undefined, { skip: templates.some(t => t.isDefault) })
  useFindAllProductsQuery(userId, { skip: products.length > 0 })
  // Playlists só carregam após priceTables E templates estarem prontos (evita race condition no buildPlaylist)
  const priceTablesReady = priceTables.length > 0 || ptLoaded
  const templatesReady   = templates.length > 0   || tmplLoaded
  useFindAllPlaylistsQuery(userId, { skip: !userId || playlists.length > 0 || !priceTablesReady || !templatesReady })
  const [createPlaylist, { isLoading: isCreating }] = useCreatePlaylistMutation()
  const [createPriceTableLayouts] = useCreatePriceTableLayoutMutation()
  const [updatePlaylist, { isLoading: isUpdating }] = useUpdatePlaylistMutation()
  const [updatePriceTableLayouts] = useUpdatePriceTableLayoutsMutation()
  const [broadcast] = useBroadcastPlaylistUpdateMutation()

  useEffect(() => {
    if (!id || id === '0') {
      dispatch(setPageTitle(locationState?.name || 'Nova Playlist'))
      return
    }
    if (playlists.length === 0) return
    const playlist = playlists.find(item => item.id === Number(id))
    if (!playlist) {
      toast.error('Falha ao carregar a playlist')
      navigate('/playlists')
      return
    }
    dispatch(setPageTitle(playlist.name))

    const resolvedItems = playlist.items.map(item => {
      let resolved = item

      // Resolve data (mídia ou tabela de preços)
      if (!resolved.data) {
        if ((item.type === 'image' || item.type === 'video') && item._mediaId) {
          const media = medias.find(m => m.id === item._mediaId)
          if (media) resolved = { ...resolved, data: media }
        }
        if (item.type === 'priceTable' && item._priceTableId) {
          const pt = priceTables.find(pt => pt.id === item._priceTableId)
          if (pt) resolved = { ...resolved, data: pt }
        }
      }

      // Resolve template (pode estar ausente mesmo quando data está ok)
      if (!resolved.template && resolved._templateId) {
        const tmpl = templates.find(t => t.id === resolved._templateId)
        if (tmpl) resolved = { ...resolved, template: tmpl }
      }

      return resolved
    })

    setItems(resolvedItems)
    setFixedTime(playlist.isTimeFixed)
    setFixedTemplate(playlist.isTemplateFixed)
    setRotation(playlist.isVertical)
    if (playlist.isTimeFixed && resolvedItems.length > 0) {
      setSelectedSeconds(resolvedItems[0].seconds)
    }
  }, [id, playlists, medias, priceTables, templates])

  // Limpa título ao sair da página
  useEffect(() => {
    return () => { dispatch(clearPageTitle()) }
  }, [])

  const totalDuration = formatDuration(
    playlistItens.reduce((acc, item) => acc + (isTimeFixed ? selectedSeconds : item.seconds), 0)
  )

  function validatePlaylist() {
    if (pageTitle.trim().length < 3) { setError('Nome muito curto'); return false }
    if (playlistItens.length === 0) { setError('Adicione pelo menos um item'); return false }
    if (!playlistItens.filter(i => i.type === 'priceTable').every(i => i.template)) {
      setError('Há tabelas sem template'); return false
    }
    setError(undefined)
    return true
  }

  async function handleSave() {
    if (!validatePlaylist()) return
    const entity: Playlist = {
      id: Number(id),
      isTemplateFixed,
      isTimeFixed,
      isVertical: rotation,
      name: pageTitle,
      userId: user?.id || 0,
      items: playlistItens.map(item => ({
        ...item,
        // Preserva o id real do banco; limpa apenas IDs temporários de drag (tempId)
        id: item.tempId ? undefined : item.id,
        template: isTemplateFixed ? selectedTemplate : item.template,
        seconds: isTimeFixed ? selectedSeconds : item.seconds,
      })),
    }
    let savedPlaylist: PlaylistDTO
    try {
      const playlist = toPlaylistDTO(entity)
      let priceTableLayouts: PriceTableLayout[] | null = null
      if (entity.items.some(i => i.type === 'priceTable')) {
        priceTableLayouts = createPriceTableDisplays(entity.items, products)
      }
      if (isEditing) {
        savedPlaylist = await updatePlaylist({ playlist }).unwrap()
      } else {
        savedPlaylist = await createPlaylist({ playlist }).unwrap()
      }
      if (priceTableLayouts && priceTableLayouts.length > 0) {
        const layoutPlaylistId = savedPlaylist.id ?? Number(id)
        priceTableLayouts = priceTableLayouts.map(l => ({
          ...l,
          id: undefined,
          playlistId: layoutPlaylistId,
        }))
        if (isEditing) {
          await updatePriceTableLayouts(priceTableLayouts).unwrap()
        } else {
          await createPriceTableLayouts({ priceTableLayouts }).unwrap()
        }
      }
      toast.success(isEditing ? 'Playlist atualizada' : 'Playlist criada')
    } catch (e) {
      // Cada service já exibe o toast de erro específico da sua etapa.
      console.error('Falha ao salvar playlist:', e)
      return // se o save falhou, não tenta enviar para a TV
    }

    // Envio para a TV (broadcast) — etapa separada, com feedback explícito.
    const playlistId = savedPlaylist.id ?? Number(id)
    if (!playlistId) {
      toast.error('Playlist salva, mas sem ID — não foi possível enviar para a TV.')
      return
    }
    try {
      await toast.promise(broadcast(playlistId.toString()).unwrap(), {
        pending: 'Enviando para a TV...',
        success: 'Enviado para a TV ✓',
        error: 'Playlist salva, mas FALHOU o envio para a TV. Clique em salvar de novo para tentar.',
      })
      navigate('/playlists')
    } catch (e) {
      // O toast.promise já mostrou o erro; mantém na página para permitir nova tentativa.
      console.error('Falha ao enviar para a TV:', e)
    }
  }

  function handleTemplateChange(e: SelectChangeEvent<number>) {
    const template = templates.find(t => t.id === e.target.value)
    if (template) {
      setSelectedTemplate(template)
      setItems(prev => prev.map(i => ({ ...i, template })))
    }
  }

  function handleMediaConfirm(ids: number[]) {
    createPlaylistItem(medias.filter(m => ids.includes(m.id)), 'image')
    setOpenMediaPicker(false)
  }

  function handlePriceTableConfirm(ids: number[]) {
    createPlaylistItem(priceTables.filter(p => ids.includes(p.id || 0)), 'priceTable')
    setOpenTablePicker(false)
  }

  function createPlaylistItem(data: Media[] | PriceTable[], type: ItemType) {
    const remaining = MAX_ITEMS - playlistItens.length
    if (remaining <= 0) { toast.warn('Playlist atingiu o máximo de itens!'); return }
    const toAdd = data.slice(0, remaining)
    if (type === 'image') createMediaItem(toAdd as Media[])
    else createPriceTableItem(toAdd as PriceTable[])
    if (toAdd.length < data.length) toast.info(`Apenas ${toAdd.length} itens adicionados (limite atingido)`)
  }

  function createMediaItem(mediaList: Media[]) {
    let count = idCounter
    const newItems = mediaList.map<PlaylistItems>((media, index) => {
      const type = media.type.split('/')[0] as ItemType
      count++
      return { data: media, type, seconds: type === 'video' ? media.sec : MIN_SEC, tempId: count, order: playlistItens.length + 1 + index }
    })
    setIdCounter(count)
    setItems(prev => [...prev, ...newItems])
  }

  function createPriceTableItem(tables: PriceTable[]) {
    let count = idCounter
    const newItems = tables.map<PlaylistItems>((table, index) => {
      count++
      return { data: table, type: 'priceTable', seconds: 5, tempId: count, order: playlistItens.length + 1 + index }
    })
    setIdCounter(count)
    setItems(prev => [...prev, ...newItems])
  }

  function onDragEnd(result: DropResult) {
    const { source, destination } = result
    if (!destination || source.index === destination.index) return
    const reordered = Array.from(playlistItens)
    const [moved] = reordered.splice(source.index, 1)
    reordered.splice(destination.index, 0, moved)
    setItems(reordered.map((item, i) => ({ ...item, order: i + 1 })))
  }

  // Casa item novo (sem id, usa tempId) OU existente (id) — evita afetar TODAS as
  // tabelas novas de uma vez (todas têm id undefined até salvar).
  const isSameItem = (i: PlaylistItems, target: PlaylistItems) =>
    i.id !== undefined ? i.id === target.id : i.tempId === target.tempId

  const handleAddCard = useCallback((n: number, item: PlaylistItems) => {
    setItems(prev => prev.map(p => isSameItem(p, item) ? { ...p, seconds: Math.min(p.seconds + n, MAX_SEC) } : p))
  }, [])

  const handleSubCard = useCallback((n: number, item: PlaylistItems) => {
    setItems(prev => prev.map(p => isSameItem(p, item) ? { ...p, seconds: Math.max(p.seconds - n, MIN_SEC) } : p))
  }, [])

  const removeCard = useCallback((item: PlaylistItems) => {
    setItems(prev => prev.filter(i => !isSameItem(i, item)).map((item, i) => ({ ...item, order: i + 1 })))
  }, [])

  const handleCardSelectTemplate = useCallback((item: PlaylistItems, template: Template) => {
    setItems(prev => prev.map(i => isSameItem(i, item) ? { ...i, template } : i))
  }, [])

  const handleScheduleChange = useCallback((item: PlaylistItems, schedule: ScheduleConfig | undefined) => {
    setItems(prev => prev.map(i =>
      (i.id !== undefined ? i.id === item.id : i.tempId === item.tempId)
        ? { ...i, schedule }
        : i
    ))
    setScheduleModalItem(null)
  }, [])

  function handleCancel() {
    setOpenCancelDialog(true)
  }

  return (
    <>
      <DialogQuiz title="Tem certeza?" text="Todas as modificações não salvas serão perdidas!"
        open={openCancelDialog}
        handleClose={(ok) => { setOpenCancelDialog(false); if (ok) navigate('/playlists') }}
        variant="warning" />

      <MediaPickerDialog open={openMediaPicker} medias={medias}
        onClose={() => setOpenMediaPicker(false)} onConfirm={handleMediaConfirm} />

      <PriceTablePickerDialog open={openTablePicker} priceTables={priceTables}
        onClose={() => setOpenTablePicker(false)} onConfirm={handlePriceTableConfirm} />

      {scheduleModalItem && (
        <ScheduleModal
          open
          initial={scheduleModalItem.schedule}
          onClose={() => setScheduleModalItem(null)}
          onSave={(schedule) => handleScheduleChange(scheduleModalItem, schedule)}
        />
      )}

      <Modal open={openHelp} onClose={() => setOpenHelp(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          bgcolor: 'background.paper', borderRadius: 2, p: 4,
          maxWidth: 600, width: '90%', maxHeight: '90%', overflowY: 'auto',
        }}>
          <ModalHeader title="Ajuda" onClose={() => setOpenHelp(false)} />
          <EditHelper />
        </Box>
      </Modal>

      {isMobile ? (
        /* ── MOBILE LAYOUT ── */
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', bgcolor: 'background.default' }}>

          {/* Barra de config colapsável */}
          <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Box onClick={() => setConfigExpanded(c => !c)}
              sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', gap: 1 }}>
              <Typography variant="body2" color="primary" fontWeight={600}>⏱ {totalDuration}</Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {rotation === 0 ? 'Sem rotação' : `${rotation}°`}
              </Typography>
              {isTimeFixed && (
                <>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary">{selectedSeconds}s fixo</Typography>
                </>
              )}
              <Box sx={{ flex: 1 }} />
              {configExpanded ? <ExpandLess sx={{ fontSize: 20, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />}
            </Box>

            <Collapse in={configExpanded}>
              <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Rotação</InputLabel>
                  <Select label="Rotação" value={rotation}
                    onChange={(e: SelectChangeEvent<number>) => setRotation(Number(e.target.value))}>
                    <MenuItem value={0}>Sem rotação</MenuItem>
                    <MenuItem value={90}>90°</MenuItem>
                    <MenuItem value={270}>270°</MenuItem>
                  </Select>
                </FormControl>
                <Box>
                  <FormControlLabel
                    control={<Switch size="small" checked={isTimeFixed} onChange={(_, c) => setFixedTime(c)} />}
                    label="Tempo fixo" />
                  {isTimeFixed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <input type="number" value={selectedSeconds} min={MIN_SEC} step={1}
                        onChange={e => setSelectedSeconds(Number(e.target.value))}
                        style={{
                          flex: 1, padding: '8px 12px', borderRadius: 8,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary, fontSize: '0.875rem', outline: 'none',
                        }} />
                      <Typography variant="body2" color="text.secondary">seg</Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  <FormControlLabel
                    control={<Switch size="small" checked={isTemplateFixed} onChange={(_, c) => setFixedTemplate(c)} />}
                    label="Template fixo" />
                  {isTemplateFixed && (
                    <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Template</InputLabel>
                      <Select label="Template" value={selectedTemplate?.id || ''} onChange={handleTemplateChange}>
                        {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Header itens */}
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" fontWeight={500}>Itens da playlist</Typography>
            <Chip label={`${playlistItens.length} / ${MAX_ITEMS}`} size="small"
              color={playlistItens.length >= MAX_ITEMS ? 'warning' : 'default'} />
            {error && <Typography variant="caption" color="error">{error}</Typography>}
          </Box>

          {/* Lista de itens scrollável */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {playlistItens.length === 0 ? (
              <EmptyState icon={PlaylistAdd} title="Playlist vazia"
                subtitle="Use os botões abaixo para adicionar itens" />
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {playlistItens.map((item, index) => (
                        <Draggable key={item.tempId ?? item.id} draggableId={(item.tempId ?? item.id)?.toString() || ''} index={index}>
                          {(provided) => (
                            <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                              sx={{
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 1.5,
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                boxShadow: theme.customShadows.shadowBox,
                              }}>
                              {item.type === 'priceTable' ? (
                                <CardTable item={item} templates={templates}
                                  setTemplate={handleCardSelectTemplate}
                                  add={handleAddCard} sub={handleSubCard} removeItem={removeCard}
                                  isFixed={isTimeFixed} isFixedTemplate={isTemplateFixed}
                                  onSchedule={setScheduleModalItem} />
                              ) : (
                                <CardMidia item={item} add={handleAddCard} sub={handleSubCard}
                                  removeItem={removeCard} isFixed={isTimeFixed}
                                  onSchedule={setScheduleModalItem} />
                              )}
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Box>

          {/* Barra sticky na base */}
          <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth variant="outlined" size="medium" startIcon={<ImageOutlined />}
                onClick={() => setOpenMediaPicker(true)} sx={{ height: 48 }}>
                Mídia
              </Button>
              <Button fullWidth variant="outlined" size="medium" startIcon={<TableChart />}
                onClick={() => setOpenTablePicker(true)} sx={{ height: 48 }}>
                Tabela
              </Button>
            </Box>
            <LoadingButton fullWidth variant="contained" color="success" size="large"
              onClick={handleSave} loading={isCreating || isUpdating}
              startIcon={isEditing ? <Edit /> : <Save />} sx={{ height: 52 }}>
              Salvar
            </LoadingButton>
            <Button fullWidth variant="outlined" size="large" onClick={handleCancel}
              startIcon={<Close />} sx={{ height: 52, color: 'text.secondary', borderColor: 'divider' }}>
              Cancelar
            </Button>
          </Box>
        </Box>

      ) : (
        /* ── DESKTOP LAYOUT ── */
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', bgcolor: 'background.default' }}>

          {/* Sidebar de configurações */}
          <Box sx={{
            width: 264, borderRight: 1, borderColor: 'divider',
            bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden',
          }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Duração total</Typography>
              <Typography variant="subtitle1" fontWeight={600} color="primary">⏱ {totalDuration}</Typography>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Rotação</InputLabel>
                <Select label="Rotação" value={rotation}
                  onChange={(e: SelectChangeEvent<number>) => setRotation(Number(e.target.value))}>
                  <MenuItem value={0}>Sem rotação</MenuItem>
                  <MenuItem value={90}>90°</MenuItem>
                  <MenuItem value={270}>270°</MenuItem>
                </Select>
              </FormControl>

              <Box>
                <FormControlLabel
                  control={<Switch size="small" checked={isTimeFixed} onChange={(_, c) => setFixedTime(c)} />}
                  label="Tempo fixo" />
                {isTimeFixed && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <input type="number" value={selectedSeconds} min={MIN_SEC} step={1}
                      onChange={e => setSelectedSeconds(Number(e.target.value))}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 8,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary, fontSize: '0.875rem', outline: 'none',
                      }} />
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>seg</Typography>
                  </Box>
                )}
              </Box>

              <Box>
                <FormControlLabel
                  control={<Switch size="small" checked={isTemplateFixed} onChange={(_, c) => setFixedTemplate(c)} />}
                  label="Template fixo" />
                {isTemplateFixed && (
                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Template</InputLabel>
                    <Select label="Template" value={selectedTemplate?.id || ''} onChange={handleTemplateChange}>
                      {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button fullWidth variant="outlined" size="small" startIcon={<ImageOutlined />}
                  onClick={() => setOpenMediaPicker(true)} sx={{ justifyContent: 'flex-start' }}>
                  Mídia
                </Button>
                <Button fullWidth variant="outlined" size="small" startIcon={<TableChart />}
                  onClick={() => setOpenTablePicker(true)} sx={{ justifyContent: 'flex-start' }}>
                  Tabela de Preço
                </Button>
              </Box>
            </Box>

            <Box sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {error && <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>{error}</Typography>}
              <LoadingButton fullWidth variant="contained" color="success"
                onClick={handleSave} loading={isCreating || isUpdating}
                startIcon={isEditing ? <Edit /> : <Save />}>
                Salvar
              </LoadingButton>
              <Button fullWidth variant="outlined" onClick={handleCancel}
                startIcon={<Close />} sx={{ color: 'text.secondary', borderColor: 'divider' }}>
                Cancelar
              </Button>
            </Box>
          </Box>

          {/* Área de cards */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={500}>Itens da playlist</Typography>
              <Chip label={`${playlistItens.length} / ${MAX_ITEMS}`} size="small"
                color={playlistItens.length >= MAX_ITEMS ? 'warning' : 'default'} />
              {error && <Typography variant="caption" color="error">{error}</Typography>}
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              {playlistItens.length === 0 ? (
                <EmptyState icon={PlaylistAdd} title="Playlist vazia"
                  subtitle="Use os botões ao lado para adicionar imagens, vídeos ou tabelas de preço" />
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="droppable" direction="horizontal">
                    {(provided) => (
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, pb: 2, minHeight: 320 }}
                        ref={provided.innerRef} {...provided.droppableProps}>
                        {playlistItens.map((item, index) => (
                          <Draggable key={item.tempId ?? item.id} draggableId={(item.tempId ?? item.id)?.toString() || ''} index={index}>
                            {(provided, snapshot) => (
                              <Box sx={{
                                width: 190, height: 280, cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                flexShrink: 0,
                                border: `1px solid ${snapshot.isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                                borderRadius: '12px', overflow: 'hidden', bgcolor: 'background.paper',
                                display: 'flex', flexDirection: 'column',
                                transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
                                transform: snapshot.isDragging ? 'scale(1.03)' : 'scale(1)',
                                boxShadow: snapshot.isDragging
                                  ? '0 12px 32px rgba(0,0,0,0.18)'
                                  : '0 4px 6px -1px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  boxShadow: snapshot.isDragging
                                    ? '0 12px 32px rgba(0,0,0,0.18)'
                                    : '0 8px 16px rgba(0,0,0,0.12)',
                                  borderColor: theme.palette.primary.light,
                                },
                              }}
                                ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                {item.type === 'priceTable' ? (
                                  <CardTable item={item} templates={templates}
                                    setTemplate={handleCardSelectTemplate}
                                    add={handleAddCard} sub={handleSubCard} removeItem={removeCard}
                                    isFixed={isTimeFixed} isFixedTemplate={isTemplateFixed}
                                    onSchedule={setScheduleModalItem} />
                                ) : (
                                  <CardMidia item={item} add={handleAddCard} sub={handleSubCard}
                                    removeItem={removeCard} isFixed={isTimeFixed}
                                    onSchedule={setScheduleModalItem} />
                                )}
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

export default React.memo(PlaylistEditPage)
